import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFreeWorkouts } from '../api/public';
import { listOfflineIds, isSavedOffline, saveForOffline, removeOffline } from '../lib/offlineFreeWorkouts';
import { isInstalledApp } from '../lib/platform';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';
import DifficultyStars from '../components/common/DifficultyStars';

export default function FreeWorkoutListPage() {
  const navigate = useNavigate();
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [offlineIds, setOfflineIds] = useState(listOfflineIds());
  const [savingId, setSavingId] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);

  // Offline-Funktionen nur in der nativen App oder installierten PWA anbieten –
  // im normalen Browser-Tab ist ein Download-Button eher verwirrend.
  const showOffline = isInstalledApp();

  useEffect(() => {
    if (!showOffline) return;
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, [showOffline]);

  useEffect(() => {
    getFreeWorkouts()
      .then(setWorkouts)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const toggleOffline = useCallback(async (id) => {
    if (isSavedOffline(id)) {
      removeOffline(id);
      setOfflineIds(listOfflineIds());
      return;
    }
    setSavingId(id);
    try {
      await saveForOffline(id);
      setOfflineIds(listOfflineIds());
    } catch (e) {
      setError(`Offline-Speichern fehlgeschlagen: ${e.message}`);
    } finally {
      setSavingId(null);
    }
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div style={{ maxWidth: 720 }}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <i className="bi bi-unlock-fill text-success fs-3" />
        <h4 className="mb-0 fw-bold">Umsonst-Workouts</h4>
      </div>
      <p className="text-muted small mb-3">
        Kostenlose, vorgefertigte Workouts – direkt abspielbar, ohne Anmeldung.
      </p>

      {showOffline && !online && (
        <div className="alert alert-warning py-2 small d-flex align-items-center gap-2">
          <i className="bi bi-wifi-off" />
          Offline – es werden nur für Offline gespeicherte Workouts abspielbar sein.
        </div>
      )}

      <AlertBanner message={error} type="danger" onDismiss={() => setError('')} />

      {workouts.length === 0 ? (
        <div className="text-center text-muted py-5">
          <i className="bi bi-collection fs-1 d-block mb-2" />
          Aktuell sind keine kostenlosen Workouts verfügbar.
        </div>
      ) : (
        <div className="list-group">
          {workouts.map((w) => {
            const saved = offlineIds.includes(Number(w.id));
            const saving = savingId === w.id;
            return (
              <div
                key={w.id}
                className="list-group-item d-flex align-items-center gap-2 py-3"
              >
                <button
                  type="button"
                  className="btn btn-link text-decoration-none text-reset flex-grow-1 d-flex align-items-center gap-3 text-start p-0 border-0"
                  onClick={() => navigate(`/presenter/${w.id}`)}
                >
                  <i className="bi bi-play-fill text-success fs-4 flex-shrink-0" />
                  <div className="flex-grow-1">
                    <div className="fw-semibold d-flex align-items-center gap-2">
                      {w.name}
                      {showOffline && saved && (
                        <span className="badge bg-success-subtle text-success border border-success-subtle" style={{ fontSize: '0.6rem' }}>
                          <i className="bi bi-cloud-check me-1" />Offline
                        </span>
                      )}
                    </div>
                    {w.description && (
                      <div className="text-muted small text-truncate" style={{ maxWidth: 420 }}>
                        {w.description}
                      </div>
                    )}
                  </div>
                  <div className="text-end flex-shrink-0">
                    <DifficultyStars value={w.difficulty} />
                    {w.duration && (
                      <div className="text-muted small mt-1">
                        <i className="bi bi-clock me-1" />
                        {w.duration} Min.
                      </div>
                    )}
                  </div>
                </button>
                {showOffline && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-reset flex-shrink-0"
                    disabled={saving}
                    title={saved ? 'Offline-Kopie entfernen' : 'Für Offline speichern'}
                    onClick={() => toggleOffline(w.id)}
                  >
                    {saving ? (
                      <span className="spinner-border spinner-border-sm" />
                    ) : (
                      <i className={saved ? 'bi bi-cloud-check-fill text-success fs-5' : 'bi bi-cloud-download fs-5'} />
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
